import React, { useState } from 'react';
import { Icon } from '@/shared/icons';
import type { PreconditionRule, ToolPrecondition } from '@troia-v3/types';
import { PreconditionRuleCard } from './PreconditionRuleCard';
import { PreconditionFormDrawer } from './PreconditionFormDrawer';

// ─── Props ───────────────────────────────────────────────────────────────────

export interface ToolItem {
  id: string;
  name: string;
  description?: string;
}

export interface ToolCategory {
  label: string;
  tools: ToolItem[];
}

interface ToolPreconditionsListProps {
  categories: ToolCategory[];
  toolPreconditions: ToolPrecondition[];
  onChange: (toolPreconditions: ToolPrecondition[]) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getConditionsForTool(
  toolId: string,
  toolPreconditions: ToolPrecondition[],
): PreconditionRule[] {
  return toolPreconditions.find((tp) => tp.toolId === toolId)?.conditions ?? [];
}

function setConditionsForTool(
  toolId: string,
  conditions: PreconditionRule[],
  toolPreconditions: ToolPrecondition[],
): ToolPrecondition[] {
  const existing = toolPreconditions.findIndex((tp) => tp.toolId === toolId);
  if (conditions.length === 0) {
    // Remove entrada se não há condições
    return toolPreconditions.filter((tp) => tp.toolId !== toolId);
  }
  if (existing >= 0) {
    const next = [...toolPreconditions];
    next[existing] = { toolId, conditions };
    return next;
  }
  return [...toolPreconditions, { toolId, conditions }];
}

// ─── Componente de ferramenta individual ─────────────────────────────────────

interface ToolRowProps {
  tool: ToolItem;
  toolPreconditions: ToolPrecondition[];
  onChangePreconditions: (next: ToolPrecondition[]) => void;
}

const ToolRow: React.FC<ToolRowProps> = ({ tool, toolPreconditions, onChangePreconditions }) => {
  const conditions = getConditionsForTool(tool.id, toolPreconditions);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<{ index: number; rule: PreconditionRule } | null>(
    null,
  );

  const handleAddRule = (rule: PreconditionRule) => {
    const next = setConditionsForTool(
      tool.id,
      [...conditions, rule],
      toolPreconditions,
    );
    onChangePreconditions(next);
  };

  const handleEditRule = (index: number, rule: PreconditionRule) => {
    const updated = conditions.map((c, i) => (i === index ? rule : c));
    onChangePreconditions(setConditionsForTool(tool.id, updated, toolPreconditions));
  };

  const handleDeleteRule = (index: number) => {
    const updated = conditions.filter((_, i) => i !== index);
    onChangePreconditions(setConditionsForTool(tool.id, updated, toolPreconditions));
  };

  const openEdit = (index: number) => {
    setEditingRule({ index, rule: conditions[index]! });
    setDrawerOpen(true);
  };

  const openAdd = () => {
    setEditingRule(null);
    setDrawerOpen(true);
  };

  return (
    <div
      className="p-4 rounded-lg border"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Header da ferramenta */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3">
          <div
            className="p-1.5 rounded-md flex-shrink-0 mt-0.5"
            style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
          >
            <Icon name="tool" className="w-3.5 h-3.5" />
          </div>
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
              {tool.name}
            </p>
            {tool.description && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                {tool.description}
              </p>
            )}
          </div>
        </div>
        {conditions.length > 0 && (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
            style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
          >
            {conditions.length} {conditions.length === 1 ? 'regra' : 'regras'}
          </span>
        )}
      </div>

      {/* Lista de regras */}
      {conditions.length > 0 && (
        <div className="space-y-2 mb-3">
          {conditions.map((rule, idx) => (
            <PreconditionRuleCard
              key={idx}
              rule={rule}
              onEdit={() => openEdit(idx)}
              onDelete={() => handleDeleteRule(idx)}
            />
          ))}
        </div>
      )}

      {/* Botão adicionar */}
      <button
        onClick={openAdd}
        className="flex items-center gap-1.5 text-xs font-medium transition-colors"
        style={{ color: 'var(--theme-primary)' }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
      >
        <Icon name="plus" className="w-3.5 h-3.5" />
        Adicionar regra
      </button>

      {/* Drawer de criação / edição */}
      <PreconditionFormDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingRule(null);
        }}
        existingRule={editingRule?.rule}
        onSave={(rule) => {
          if (editingRule !== null) {
            handleEditRule(editingRule.index, rule);
          } else {
            handleAddRule(rule);
          }
        }}
      />
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────

export const ToolPreconditionsList: React.FC<ToolPreconditionsListProps> = ({
  categories,
  toolPreconditions,
  onChange,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    categories.forEach((cat) => { init[cat.label] = true; });
    return init;
  });

  const toggleCategory = (label: string) => {
    setExpandedCategories((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  if (categories.every((cat) => cat.tools.length === 0)) {
    return (
      <div
        className="rounded-lg border p-8 text-center"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <Icon name="tool" className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--theme-muted-text)' }} />
        <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-card-text)' }}>
          Nenhuma ferramenta configurada
        </p>
        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          Habilite capacidades ou adicione ações customizadas na aba "Ferramentas" para configurar pré-condições.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {categories
        .filter((cat) => cat.tools.length > 0)
        .map((cat) => {
          const isExpanded = expandedCategories[cat.label] ?? true;
          const ruleCount = cat.tools.reduce(
            (acc, t) => acc + getConditionsForTool(t.id, toolPreconditions).length,
            0,
          );

          return (
            <div key={cat.label}>
              {/* Header da categoria */}
              <button
                onClick={() => toggleCategory(cat.label)}
                className="w-full flex items-center justify-between py-2 mb-3 border-b"
                style={{ borderColor: 'var(--theme-card-border)' }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                    {cat.label}
                  </span>
                  <span
                    className="text-xs px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: 'var(--theme-card-border)',
                      color: 'var(--theme-muted-text)',
                    }}
                  >
                    {cat.tools.length}
                  </span>
                  {ruleCount > 0 && (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded font-medium"
                      style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
                    >
                      {ruleCount} {ruleCount === 1 ? 'regra' : 'regras'}
                    </span>
                  )}
                </div>
                <Icon
                  name={isExpanded ? 'chevron-up' : 'chevron-down'}
                  className="w-4 h-4"
                  style={{ color: 'var(--theme-muted-text)' }}
                />
              </button>

              {/* Ferramentas da categoria */}
              {isExpanded && (
                <div className="space-y-3">
                  {cat.tools.map((tool) => (
                    <ToolRow
                      key={tool.id}
                      tool={tool}
                      toolPreconditions={toolPreconditions}
                      onChangePreconditions={onChange}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};
